import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import { InventoryItem } from '@/api/services';
import { useCreatePurchaseOrder, useSuppliersList, useInventoryList } from '@/api/hooks';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

interface LineItem {
  itemId: string;
  item?: InventoryItem;
  quantity: number;
  unitPrice: number;
}

interface POFormData {
  supplierId: string;
  expectedDate: string;
  currency: string;
  notes: string;
  items: LineItem[];
}

const emptyFormData: POFormData = {
  supplierId: '',
  expectedDate: '',
  currency: 'USD',
  notes: '',
  items: [],
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

export default function PurchaseOrdersNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState<POFormData>(emptyFormData);

  const { data: suppliersData, isLoading: loadingSuppliers } = useSuppliersList({
    isActive: true,
    limit: 500,
  });
  const { data: inventoryData, isLoading: loadingItems } = useInventoryList({ limit: 500 });
  const suppliers = suppliersData?.data ?? [];
  const inventoryItems = inventoryData?.data ?? [];

  const createMutation = useCreatePurchaseOrder();

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { itemId: '', quantity: 1, unitPrice: 0 }],
    });
  };

  const handleRemoveItem = (index: number) => {
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  const handleItemChange = (index: number, field: keyof LineItem, value: string | number) => {
    const newItems = [...formData.items];
    if (field === 'itemId') {
      const selectedItem = inventoryItems.find((item) => item.id === value);
      newItems[index] = {
        ...newItems[index],
        itemId: value as string,
        item: selectedItem,
        unitPrice: selectedItem?.unitCost || 0,
      };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () =>
    formData.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.supplierId) {
      toast.error('Please select a supplier');
      return;
    }
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    if (formData.items.some((item) => !item.itemId || item.quantity <= 0)) {
      toast.error('Please complete all line items');
      return;
    }

    createMutation.mutate(
      {
        companyId: user?.companyId || '',
        supplierId: formData.supplierId,
        expectedDate: formData.expectedDate || undefined,
        currency: formData.currency,
        notes: formData.notes || undefined,
        items: formData.items.map((item) => ({
          itemId: item.itemId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
        })),
      },
      {
        onSuccess: () => {
          toast.success('Purchase order created successfully');
          navigate('/purchase-orders');
        },
      }
    );
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="Create Purchase Order"
        description="Supplier and line items required to raise an order for approval."
        actions={
          <Button variant="outline" onClick={() => navigate('/purchase-orders')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Purchase Orders
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Order Details */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Order Details</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="supplier">Supplier *</Label>
              <Select
                value={formData.supplierId}
                onValueChange={(value) => setFormData({ ...formData, supplierId: value })}
                disabled={loadingSuppliers}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingSuppliers ? 'Loading...' : 'Select supplier'} />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name} ({supplier.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expectedDate">Expected Delivery</Label>
                <Input
                  id="expectedDate"
                  type="date"
                  value={formData.expectedDate}
                  onChange={(e) => setFormData({ ...formData, expectedDate: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>

          {/* Line Items */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold">Line Items</h3>
              <Button type="button" variant="outline" size="sm" onClick={handleAddItem}>
                <Plus className="mr-2 h-3 w-3" />
                Add Item
              </Button>
            </div>
            <Separator />

            {formData.items.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center">
                <Package className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No items added yet.</p>
                <Button type="button" variant="link" size="sm" onClick={handleAddItem}>
                  Add your first item
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="rounded-lg border p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <Label className="text-xs text-muted-foreground">Item {index + 1}</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <Select
                      value={item.itemId}
                      onValueChange={(value) => handleItemChange(index, 'itemId', value)}
                      disabled={loadingItems}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder={loadingItems ? 'Loading...' : 'Select item'} />
                      </SelectTrigger>
                      <SelectContent>
                        {inventoryItems.map((invItem) => (
                          <SelectItem key={invItem.id} value={invItem.id}>
                            {invItem.name} ({invItem.sku})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)
                          }
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Unit Price ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unitPrice}
                          onChange={(e) =>
                            handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)
                          }
                          className="h-9"
                        />
                      </div>
                    </div>
                    <div className="text-right text-sm font-medium">
                      Subtotal: {formatCurrency(item.quantity * item.unitPrice)}
                    </div>
                  </div>
                ))}

                {formData.items.length > 0 && (
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className="font-medium">Total</span>
                    <span className="text-lg font-semibold">
                      {formatCurrency(calculateTotal())}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/purchase-orders')}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Purchase Order'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
